import { message } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../../axios/axios-instance";
import api from "../../hooks/api";
import { pathAPI } from "../../utils/pathApi";
import ManageTranslationModal, { ManageTranslationItem } from "./ManageTranslationModal";

interface EndpointTranslationModalProps {
  open: boolean;
  onClose: () => void;
  /** GET returns `data.data.translations` (one row per language); PUT receives the full list. */
  endpoint: string;
  title: string;
  subtitle?: string;
  /** Source text for auto translate when no language has been filled in yet */
  fallbackSourceText?: string;
}

interface AutoTranslateResponse {
  data?: {
    translations?: Record<string, string>;
  };
}

/** ManageTranslationModal wired to a `<entity>/{id}/translations` endpoint (system config, message codes...). */
const EndpointTranslationModal = ({
  open,
  onClose,
  endpoint,
  title,
  subtitle,
  fallbackSourceText,
}: EndpointTranslationModalProps) => {
  const [translations, setTranslations] = useState<ManageTranslationItem[]>([]);
  const [loading, setLoading] = useState(false);
  // Endpoint that `translations` belongs to; mismatch = stale for the open item.
  const [loadedEndpoint, setLoadedEndpoint] = useState<string | null>(null);
  // Bumps per fetch so a late response from a previous item is ignored.
  const requestSeqRef = useRef(0);

  const fetchTranslations = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const response = await axiosInstance.get(endpoint);
      if (seq !== requestSeqRef.current) return;
      setTranslations(response.data?.data?.translations ?? []);
      setLoadedEndpoint(endpoint);
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      console.error("Error fetching translations:", error);
      message.error("Failed to fetch translations");
      // Mark loaded so a failed request does not leave the modal loading forever.
      setTranslations([]);
      setLoadedEndpoint(endpoint);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    if (open) fetchTranslations();
  }, [open, fetchTranslations]);

  // Must be false until data for THIS endpoint is loaded: the inner modal
  // hydrates once per open, and stale hydration wins permanently (its
  // "only fill empty fields" guard never overwrites the wrong values).
  const ready = !loading && loadedEndpoint === endpoint;

  const handleSave = async (items: ManageTranslationItem[]) => {
    try {
      await axiosInstance.put(
        endpoint,
        items.map((item) => ({
          translation_id: item.translation_id,
          translation_name: item.translation_name || null,
          language_id: item.language_id,
          language_code: item.language_code,
        }))
      );
    } catch (error) {
      console.error("Error saving translations:", error);
      message.error("Failed to save translations");
      throw error;
    }
    message.success("Translations saved successfully");
  };

  // Same auto-translate endpoint as the category translation modal
  const handleAutoTranslate = useCallback(async (text: string, sourceLanguageCode: string) => {
    const response = await api.POST<AutoTranslateResponse>(
      `${pathAPI.categoryDetail}/translations/auto`,
      { text, source_language_code: sourceLanguageCode },
      {},
      true
    );
    return response?.data?.translations || {};
  }, []);

  return (
    <ManageTranslationModal
      open={open}
      title={title}
      subtitle={subtitle}
      languages={[]}
      translations={translations}
      loading={!ready}
      ready={ready}
      onCancel={onClose}
      fallbackSourceText={fallbackSourceText}
      onSave={handleSave}
      onAutoTranslate={handleAutoTranslate}
    />
  );
};

export default EndpointTranslationModal;
